import React, {FC, forwardRef, memo, RefObject, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {
    GoogleMap,
    InfoWindow,
    LoadScriptNext,
    Marker,
    MarkerClusterer,
    OverlayView,
} from '@react-google-maps/api';
import {Cluster} from "@react-google-maps/marker-clusterer/src/Cluster";
import {Clusterer} from "@react-google-maps/marker-clusterer/src/Clusterer";
import {
    GOOGLE_API_KEY,
    GOOGLE_MAP_DEFAULT_PADDING_HORIZONTAL,
    GOOGLE_MAP_DEFAULT_PADDING_VERTICAL,
    GOOGLE_MAP_MARKER_HEIGHT,
    GOOGLE_MAP_MARKER_WIDTH,
    GOOGLE_MAP_STYLE_CONFIGURATION,
    GOOGLE_MAP_ZOOM_LEVEL_ON_SINGLE_PIN
} from '@montana/constants/google-map';
import {createLocation, getBoundsOfPins} from '@montana/pages/store-locator-page/store-locator.service';
import {isNotNullOrUndefined} from '@montana/helpers/type.helpers';
import {ILocation, IMapRef} from './map';
import {IEnhancedStore} from '@montana/models/dev';
import {Link} from '@montana/shared/link';
import {TrackingTypes} from '@montana/helpers/tracking.helper';
import {Translate} from '@montana/shared/translate';
import {TabletAndLarger} from '@montana/shared/responsive';
// eslint-disable-next-line no-undef
import LatLngBoundsLiteral = google.maps.LatLngBoundsLiteral;

// Defined here to avoid reloading scripts because of new array-dependency!
const MAP_CENTER_AARHUS = {lat: 56.155349, lng: 10.206626};
const MINIMUM_CLUSTER_SIZE = 3;

let debounceTimeOutBounds: any;
const librariesToLoad: any = ['places'] ;

export interface IMapProps {
    visibleStores: IEnhancedStore[];
    ref?: RefObject<IMapRef>;
    onStoreSelected?: (store: IEnhancedStore) => void;
    onBoundsChanged?: (worldCorners: LatLngBoundsLiteral) => void;
    isSearchedStores: boolean;
    isFetchingStores: boolean;
}

interface IInfoWinProps {
    store?: IEnhancedStore;
    visible: boolean;
}

const MapComponent: FC<IMapProps> = memo(forwardRef(({
    visibleStores,
    onStoreSelected,
    onBoundsChanged,
    isSearchedStores,
    isFetchingStores
}: IMapProps, ref) => {
    const [mapInstance, _setMapInstance] = useState<google.maps.Map | null>(null);
    const [clustererInstance, setClustererInstance] = useState<Clusterer>();
    const [markerLabels, setMarkerLabels] = useState<JSX.Element[]>();
    const [selectedStore, setSelectedStore] = useState<IInfoWinProps>({visible: false});

    const mapInstanceRef = useRef(mapInstance);
    const initialMapRender = useRef<boolean>(true);
    const getStoresFromBoundsRef = useRef<boolean>(true);
    const useFitPinsToMap = useRef<boolean>(false);

    const setGetStoresFromBounds = (bool: boolean) => {
        getStoresFromBoundsRef.current = bool;
    }

    const setMapInstance = (map: google.maps.Map) => {
        _setMapInstance(map);
        mapInstanceRef.current = map;
    }
    useImperativeHandle(ref, () => ({
        zoomToStore: (store: IEnhancedStore) => zoomToStore(store),
        fitMapToPins: (visibleStores: IEnhancedStore[]) => {
            return fitMapToPins(visibleStores.map(s => createLocation(s)).filter(isNotNullOrUndefined));
        }
    }))

    useEffect(() => {
        clustererInstance?.repaint();
    }, [visibleStores])

    useEffect(() => {
        if (isSearchedStores && !isFetchingStores) {
            useFitPinsToMap.current = true;

            setGetStoresFromBounds(false);

            fitMapToPins(visibleStores.map(s => createLocation(s)).filter(isNotNullOrUndefined));
        }
    }, [isSearchedStores, isFetchingStores, visibleStores])

    const fitMapToPins = (pinsToFit: ILocation[]): void => {
        if (initialMapRender.current || useFitPinsToMap.current) {
            mapInstanceRef.current?.fitBounds(
                getBoundsOfPins(pinsToFit)
            );
            useFitPinsToMap.current = false;
        }
    }

    const showPinsInCluster = (cluster: Cluster): void => {

        const fitPositions = [] as ILocation[];

        setGetStoresFromBounds(false);

        if (mapInstanceRef.current) {
            const markers = cluster.getMarkers();

            if (markers) {
                markers.forEach((marker) => {
                    const position = marker.getPosition();

                    if (position) {
                        fitPositions.push({
                            lat: position.lat(),
                            lng: position.lng(),
                        });
                    }
                });
            }
            useFitPinsToMap.current = true;
            fitMapToPins(fitPositions);
        }
    };

    const zoomToStore = (store: IEnhancedStore, map?: google.maps.Map): void => {

        const mapToUse = map || mapInstance;

        if (!mapToUse || !store.location) {
            return;
        }

        const {lat, lng} = store.location;
        mapToUse.panTo({lat, lng});

        const currentZoomLevel = mapToUse.getZoom();
        if (!!currentZoomLevel && currentZoomLevel < GOOGLE_MAP_ZOOM_LEVEL_ON_SINGLE_PIN) {
            mapToUse.setZoom(GOOGLE_MAP_ZOOM_LEVEL_ON_SINGLE_PIN);

        }
    }

    function boundsChanged() {
        const mapInstance = mapInstanceRef.current;

        if (getStoresFromBoundsRef.current) {
            const worldCorners = mapInstance?.getBounds()?.toJSON();

            clearTimeout(debounceTimeOutBounds);

            debounceTimeOutBounds = setTimeout(() => {
                if (!worldCorners) {
                    return
                }
                if (!initialMapRender.current) {
                    !!onBoundsChanged && onBoundsChanged(worldCorners);
                }
                initialMapRender.current = false;
            }, 200);
        }
        getStoresFromBoundsRef.current = true;

    }

    const markerLabelsHandler = (clusterer: Clusterer) => {
        // @ts-ignore
        if (!clusterer?.clusters || !mapInstance || (!!mapInstanceRef || mapInstanceRef.current?.getZoom() < 10)) {
            setMarkerLabels([]);
        } else {
            const markerLabelsList: JSX.Element[] = [];
            const allClusters = clusterer.clusters;
            allClusters.forEach((cluster) => {
                const markersInCluster = cluster.getMarkers();
                markersInCluster.forEach((marker) => {
                    if (!!marker.getPosition() && markersInCluster.length < MINIMUM_CLUSTER_SIZE) {
                        markerLabelsList.push(
                            <OverlayView
                                key={Math.floor(Math.random() * 10000000)}
                                position={marker.getPosition() as google.maps.LatLng}
                                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                getPixelPositionOffset={() => ({x: 30, y: -40})}
                            >
                                <div
                                    style={{
                                        fontSize: '16px',
                                        fontFamily: 'CircularProBookItalic", sans-serif',
                                        lineHeight: '20px',
                                        color: `#E13D2F`,
                                    }}>
                                    {marker.getTitle()}
                                </div>
                            </OverlayView>)
                    }
                });
            });
            setMarkerLabels(markerLabelsList);
        }
    }

    const setStore = (store: IEnhancedStore) => {
        if (onStoreSelected) {
            onStoreSelected(store);
        }

        setSelectedStore({
            store,
            visible: true
        });
    }

    const toggleInfoWin = () => {
        setSelectedStore({
            ...selectedStore,
            visible: false
        })
    }

    return (
        <div>
            <LoadScriptNext
                id="script-loader"
                googleMapsApiKey={GOOGLE_API_KEY}
                libraries={librariesToLoad}
            >
                <GoogleMap
                    mapContainerClassName="store-locator__map"
                    zoom={3}
                    center={MAP_CENTER_AARHUS}
                    options={{
                        maxZoom: 18,
                        minZoom: 5,
                        mapTypeControl: false,
                        streetViewControl: false,
                        fullscreenControl: false,
                        clickableIcons: false,
                        zoomControl: true,
                        styles: GOOGLE_MAP_STYLE_CONFIGURATION,
                    }}
                    onLoad={(map: google.maps.Map) => {
                        setMapInstance(map);
                        map.addListener('idle', () => {
                            boundsChanged();
                        });
                        map.fitBounds(
                            getBoundsOfPins(visibleStores.map(s => createLocation(s)).filter(isNotNullOrUndefined)),
                            {
                                top: GOOGLE_MAP_DEFAULT_PADDING_VERTICAL,
                                bottom: GOOGLE_MAP_DEFAULT_PADDING_VERTICAL,
                                right: GOOGLE_MAP_DEFAULT_PADDING_HORIZONTAL,
                                left: GOOGLE_MAP_DEFAULT_PADDING_HORIZONTAL,
                            },
                        );
                    }}
                >
                    {selectedStore && selectedStore.visible && (
                        <TabletAndLarger>
                            <InfoWindow
                                position={selectedStore?.store?.location}
                                options={{
                                    pixelOffset: {
                                        height: -55,
                                        width: 0,
                                        equals(other: google.maps.Size): boolean {
                                            return other.width === 0;
                                        }
                                    }
                                }}
                                onCloseClick={() => toggleInfoWin()}
                            >
                                <div className="info-win">
                                    <span className="info-win__title">{selectedStore.store?.name}</span>
                                    <p className="info-win__content-section">
                                        {selectedStore.store?.address}<br/>
                                        {selectedStore.store?.zipCode} {selectedStore.store?.city}<br/>
                                        {selectedStore.store?.country}
                                    </p>
                                    <p className="info-win__content-section">
                                        <Link
                                            withStroke
                                            to={encodeURI(`https://www.google.com/maps/search//?api=1&query=${selectedStore.store?.name},${selectedStore.store?.address},${selectedStore.store?.zipCode},${selectedStore.store?.city},${selectedStore.store?.country}`)}
                                            trackingType={TrackingTypes.OutgoingLink}
                                            trackingPayload={{
                                                event: 'dealerGoToGoogleMaps',
                                                city: selectedStore.store?.city || '',
                                                postalCode: selectedStore.store?.zipCode || '',
                                                country: selectedStore.store?.country || '',
                                                phone: selectedStore.store?.phoneNumber || '',
                                            }}
                                        >
                                            <Translate value={(t): string => t.storeLocator.openInGoogleMaps}
                                                defaultValue="[Open in Google Maps]"/>
                                        </Link>
                                    </p>
                                    <p>
                                        {!!selectedStore.store?.websiteUrl && (
                                            <><Link
                                                className="store-item__website"
                                                to={selectedStore.store?.websiteUrl}
                                                withStroke
                                                trackingType={TrackingTypes.Other}
                                                trackingPayload={{
                                                    event: 'dealerClick',
                                                    city: selectedStore.store?.city || '',
                                                    postalCode: selectedStore.store?.zipCode || '',
                                                    country: selectedStore.store?.country || '',
                                                    phone: selectedStore.store?.phoneNumber || '',
                                                }}
                                            >
                                                {selectedStore.store?.websiteText ?? selectedStore.store?.websiteUrl}
                                            </Link><br/></>
                                        )}
                                        {!!selectedStore.store?.email && (
                                            <><Link
                                                className="store-item__email"
                                                to={`mailto:${selectedStore.store.email}`}
                                                withStroke
                                                trackingType={TrackingTypes.Other}
                                                trackingPayload={{
                                                    event: 'dealerClick',
                                                    city: selectedStore.store.city || '',
                                                    postalCode: selectedStore.store.zipCode || '',
                                                    country: selectedStore.store.country || '',
                                                    phone: selectedStore.store.phoneNumber || '',
                                                }}
                                            >
                                                {selectedStore.store.email}
                                            </Link><br/></>
                                        )}
                                        {!!selectedStore.store?.phoneNumber && (
                                            <><Link
                                                className="store-item__email"
                                                to={`tel:${selectedStore.store.phoneNumber}`}
                                                withStroke
                                                trackingType={TrackingTypes.Other}
                                                trackingPayload={{
                                                    event: 'dealerClick',
                                                    city: selectedStore.store.city || '',
                                                    postalCode: selectedStore.store.zipCode || '',
                                                    country: selectedStore.store.country || '',
                                                    phone: selectedStore.store.phoneNumber || '',
                                                }}
                                            >
                                                {selectedStore.store.phoneNumber}
                                            </Link><br/></>
                                        )}
                                    </p>
                                </div>
                            </InfoWindow>
                        </TabletAndLarger>
                    )}
                    <MarkerClusterer
                        averageCenter
                        ignoreHidden
                        zoomOnClick={false}
                        minimumClusterSize={MINIMUM_CLUSTER_SIZE}
                        clusterClass="store-locator__cluster"
                        onClusteringEnd={markerLabelsHandler}
                        styles={[{
                            textColor: "#fff",
                            fontFamily: "inherit",
                            url: "",
                            height: 40,
                            width: 40,
                        }]}
                        onClick={(cluster: Cluster) => showPinsInCluster(cluster)}
                        onLoad={(clusterer: Clusterer) => setClustererInstance(clusterer)}
                    >
                        {(clusterer: Clusterer): JSX.Element[] | false => (
                            !!visibleStores.length && visibleStores.map((store, pinIndex): JSX.Element => {
                                return !!store.location
                                    ? <Marker
                                        key={`marker-${pinIndex}`}
                                        visible={visibleStores.some(vs => vs.id === store.id)}
                                        position={store.location}
                                        clusterer={clusterer}
                                        title={store.name}
                                        icon={{
                                            url: `/assets/icons/${'placemap.svg'}`,
                                            scaledSize: new google.maps.Size(GOOGLE_MAP_MARKER_WIDTH, GOOGLE_MAP_MARKER_HEIGHT), // eslint-disable-line
                                        }}
                                        onClick={() => setStore(store)}
                                        //onUnmount={marker => marker.unbindAll()}
                                    />
                                    : <></>;
                            })
                        )}
                    </MarkerClusterer>
                    {markerLabels}

                </GoogleMap>
            </LoadScriptNext>
        </div>
    );
}));

export default MapComponent;
